'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';

import { useCurrentUserStore } from '@/services/auth/store';
import { useGetForumDigestSettings, type ForumDigestSettings } from '@/services/forum/hooks/useGetForumDigestSettings';
import { useUpdateForumDigestSettings } from '@/services/forum/hooks/useUpdateForumDigestSettings';
import { useSettingsAnalytics } from '@/analytics/settings.analytics';
import type { FollowAnalyticsSource } from '@/analytics/follow.analytics';
import type { ISuggestedTeam, ITeamNewsPopularItem } from '@/types/team-news.types';

import { SHOW_POPULAR_THIS_WEEK } from '@/components/page/home/TeamNews/constants';

import { TeamsToFollowCard } from './components/TeamsToFollowCard';
import { PopularThisWeekCard } from './components/PopularThisWeekCard';

import s from './NewsRail.module.scss';

const ArrowRight = () => (
  <svg width="11" height="10" viewBox="0 0 11 10" fill="none" aria-hidden="true">
    <path
      d="M10.7455 5.06028L6.80805 8.99778C6.68476 9.12106 6.51755 9.19032 6.3432 9.19032C6.16885 9.19032 6.00164 9.12106 5.87836 8.99778C5.75508 8.8745 5.68582 8.70729 5.68582 8.53294C5.68582 8.35859 5.75508 8.19138 5.87836 8.06809L8.69531 5.25223H0.65625C0.482202 5.25223 0.315282 5.18309 0.192211 5.06002C0.0691404 4.93695 0 4.77003 0 4.59598C0 4.42193 0.0691404 4.25501 0.192211 4.13194C0.315282 4.00887 0.482202 3.93973 0.65625 3.93973H8.69531L5.87945 1.12223C5.75617 0.998948 5.68691 0.831738 5.68691 0.657388C5.68691 0.483038 5.75617 0.315829 5.87945 0.192544C6.00274 0.0692602 6.16995 1.83708e-09 6.3443 0C6.51865 -1.83708e-09 6.68586 0.0692602 6.80914 0.192544L10.7466 4.13004C10.8078 4.19109 10.8564 4.26363 10.8894 4.34349C10.9225 4.42335 10.9395 4.50895 10.9394 4.59539C10.9393 4.68183 10.9221 4.76739 10.8888 4.84717C10.8556 4.92695 10.8069 4.99937 10.7455 5.06028Z"
      fill="currentColor"
    />
  </svg>
);

const MailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3 6l7 5 7-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const buildDefaultDigestSettings = (uid: string): ForumDigestSettings => ({
  forumDigestEnabled: false,
  forumDigestFrequency: 7,
  forumDigestForumEnabled: true,
  // Both content types default to on: this fallback is used as the subscribe
  // payload when the real settings haven't loaded yet, and a member without
  // forum access who subscribed with news off would get an empty (never-sent)
  // digest.
  forumDigestNewsEnabled: true,
  forumDigestLastSentAt: null,
  memberExternalId: null,
  memberUid: uid,
});

interface NewsRailProps {
  /** Fetched server-side (like Settings > Email) so the subscribed/not-subscribed
   * card matches on first paint — no client-side flash while the query resolves. */
  initialDigestSettings?: ForumDigestSettings | null;
  /** Server-ranked "Popular this week" (GET /v1/team-news/popular), fetched SSR in
   * app/home/page.tsx and threaded through TeamNews.tsx. */
  popularItems?: ITeamNewsPopularItem[];
  isLoadingSuggestedTeams?: boolean;
  followedTeamUids?: Set<string>;
  /** Same followedTeamUids/handleFollowToggle TeamNews.tsx already threads to
   * NewsGroupCard — NewsRail renders as a sibling of the feed within TeamNews.tsx's
   * own tree, not a separate route, so this is an ordinary prop, not a new store. */
  onFollowToggle?: (
    teamUid: string,
    teamName: string,
    isCurrentlyFollowing: boolean,
    source?: FollowAnalyticsSource,
    meta?: { position?: number; reason?: string },
  ) => void;
  /** Threaded straight through to PopularThisWeekCard, unchanged — TeamNews.tsx
   * is the single caller and always provides it, so there's no fallback here. */
  onPopularItemClick: (item: ITeamNewsPopularItem, position: number) => void;
  /** False below 1200px, where TeamNews renders Teams-to-follow and Popular as
   *  horizontal scrollers in the feed column instead. Keeps exactly one instance
   *  of each module on screen; the digest card is unaffected either way. */
  renderModules?: boolean;
  /** Computed by TeamNews (one owner, since the scrollers need the same list and
   *  the same delayed-hide-after-follow behaviour). */
  visibleSuggestions?: ISuggestedTeam[];
}

const EMPTY_FOLLOWED_UIDS = new Set<string>();

/** Teams-to-follow + Popular-this-week (v1) alongside the digest subscribe CTA
 * (reuses the same forum-digest mutation as Settings > Email, weekly frequency). */
export function NewsRail({
  initialDigestSettings = null,
  popularItems = [],
  isLoadingSuggestedTeams = false,
  followedTeamUids = EMPTY_FOLLOWED_UIDS,
  onFollowToggle,
  onPopularItemClick,
  renderModules = true,
  visibleSuggestions = [],
}: NewsRailProps) {
  const router = useRouter();
  const { currentUser, isHydrated } = useCurrentUserStore();
  const analytics = useSettingsAnalytics();
  const { mutate } = useUpdateForumDigestSettings();

  const defaultSettings = useMemo(
    () => initialDigestSettings ?? buildDefaultDigestSettings(currentUser?.uid ?? ''),
    [initialDigestSettings, currentUser?.uid],
  );
  const { data } = useGetForumDigestSettings(currentUser?.uid, defaultSettings);
  // Known server-side up front (via initialDigestSettings), so this doesn't
  // need to wait on client auth-store hydration the way the button below does.
  const isSubscribed = Boolean(data?.forumDigestEnabled);

  const handleSubscribeClick = () => {
    if (!currentUser?.uid) {
      router.push(`${window.location.pathname}${window.location.search}#login`, { scroll: false });
      return;
    }

    const payload = {
      ...(data ?? defaultSettings),
      forumDigestEnabled: true,
      forumDigestFrequency: 7 as const,
      forumDigestNewsEnabled: true,
    };
    mutate(
      { uid: currentUser.uid, payload },
      {
        onSuccess: () => analytics.onForumDigestOptionSelect({ ...payload, source: 'home-feed' }),
        onError: () => analytics.onForumDigestSaveFailed({ attemptedFrequency: 'weekly', source: 'home-feed' }),
      },
    );
  };

  // Whether to mount TeamsToFollowCard lives here (not inside the card itself) so
  // AnimatePresence can see it disappear from this children array and play the
  // card's own exit animation instead of it just vanishing — e.g. when the loader
  // resolves to zero suggestions. `layout` on every card below lets the rest of
  // the rail smoothly reflow into the space that frees up, rather than jumping.
  //
  // `renderModules` is the sub-desktop switch: below 1200px the grid drops this
  // rail's column and TeamNews renders these two as horizontal scrollers inside
  // the feed column instead. Exactly one surface is ever mounted, so the digest
  // card below — which has no scroller counterpart — is unaffected.
  const showTeamsToFollow =
    renderModules && Boolean(onFollowToggle) && (isLoadingSuggestedTeams || visibleSuggestions.length > 0);
  const showPopular = SHOW_POPULAR_THIS_WEEK && renderModules && popularItems.length > 0;

  // The view-once analytics used to live here. They moved up to TeamNews (see
  // useFeedModulesViewAnalytics): with two possible surfaces, an effect next to
  // the cards would either double-fire or only ever report desktop.

  return (
    <aside className={s.rail} aria-label="News feed sidebar">
      <AnimatePresence initial={false}>
        {showTeamsToFollow && (
          <TeamsToFollowCard
            key="teams-to-follow"
            suggestions={visibleSuggestions}
            isLoading={isLoadingSuggestedTeams}
            followedTeamUids={followedTeamUids}
            onFollowToggle={onFollowToggle!}
          />
        )}
        {showPopular && (
          <PopularThisWeekCard key="popular-this-week" items={popularItems} onPopularItemClick={onPopularItemClick} />
        )}

        {/* The digest CTA is open to everyone — members without forum access
            get a network-news-only digest (forum posts are suppressed
            server-side at send time). */}
        {isSubscribed ? (
          <motion.section
            key="digest-subscribed"
            layout
            className={s.subscribedCard}
            aria-label="You're subscribed to the news digest"
          >
            <span className={s.iconBadge} aria-hidden="true">
              <MailIcon />
            </span>
            <p className={s.whyTitle}>You&apos;re subscribed to the Digest</p>
            <p className={s.whyBody}>Change frequency or unsubscribe anytime in Settings.</p>
            <Link href="/settings/email" className={s.manageLink}>
              <span>Manage in Settings</span>
              <ArrowRight />
            </Link>
          </motion.section>
        ) : (
          <motion.section key="digest-promo" layout className={s.digestCard} aria-label="Subscribe to the news digest">
            <p className={s.digestTitle}>Get network news Digest</p>
            <p className={s.digestBody}>
              A news digest covering raises, launches, and milestones across the network, straight to your inbox.
            </p>
            {isHydrated && (
              <button type="button" className={s.digestBtn} onClick={handleSubscribeClick}>
                <span>Subscribe</span>
              </button>
            )}
          </motion.section>
        )}
      </AnimatePresence>
    </aside>
  );
}
